import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { PiMagnifyingGlassLight, PiArrowRightLight, PiMagnifyingGlass } from 'react-icons/pi';
import { GROUP_ICON, GROUP_LABEL, EMPTY_DYNAMIC, buildSearchGroups } from './searchGroups';

// One dropdown, seven possible sources: a static site-page index, the
// taxonomy's categories/subcategories, and the Help Center's articles, all
// matched entirely client-side (instant — no network round trip), plus four
// live DB lookups behind a single debounced call to /api/search (freelancers,
// services, jobs, courses).

// What the topbar placeholder cycles through. Every one is a phrase that,
// typed exactly as shown, returns live services on Hire Talent (checked
// against the listings on 2026-10-03). Keep it that way: an example that
// finds nothing is worse than no example. A bare "Search" told a visitor
// nothing about the site; Fiverr and Etsy both lead with an example.
const EXAMPLES = [
  'logo design',
  'video editing',
  'event photography',
  'website',
  'motion graphics',
  'podcast',
  'flyer design',
  'graduation photoshoot',
];
const EXAMPLE_MS = 3500;

// Bolds the first place the query appears in a result, so it's clear why
// that result came up. Exported for the full results page, which marks
// matches the same way.
export function Highlight({ text, query }) {
  const at = query ? text.toLowerCase().indexOf(query.toLowerCase()) : -1;
  if (at < 0) return text;
  return (
    <>
      {text.slice(0, at)}
      <strong className="font-bold text-press-ink">{text.slice(at, at + query.length)}</strong>
      {text.slice(at + query.length)}
    </>
  );
}

Highlight.propTypes = { text: PropTypes.string.isRequired, query: PropTypes.string };

export default function SiteSearch({ user, variant, placeholder }) {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  // Unique per instance: the Home hero and the topbar can both be mounted.
  const baseId = `uf-search-${useId().replace(/:/g, '')}`;

  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  // Nothing fetches extra results any more; buildSearchGroups still takes the slot.
  const dynamic = EMPTY_DYNAMIC;
  const [highlighted, setHighlighted] = useState(-1);
  const [exampleIdx, setExampleIdx] = useState(0);

  const trimmed = query.trim();

  const groups = useMemo(
    () => buildSearchGroups({ query: trimmed, user, dynamic, quickLinksWhenEmpty: true }),
    [trimmed, user, dynamic]
  );

  const flat = useMemo(() => groups.flatMap((g) => g.items), [groups]);

  useEffect(() => {
    setHighlighted(-1);
  }, [flat.length, trimmed]);

  const goTo = (path) => {
    navigate(path);
    setQuery('');
    setOpen(false);
    setHighlighted(-1);
    inputRef.current?.blur();
  };

  function handleSubmit(e) {
    e.preventDefault();
    // Enter with a highlighted suggestion goes straight there. Enter with
    // nothing highlighted falls back to a real results page — jumping
    // straight to whatever happened to rank first would be surprising for a
    // query someone meant as a broad browse, not a lookup.
    if (highlighted >= 0 && flat[highlighted]) {
      goTo(flat[highlighted].path);
      return;
    }
    showAllResults();
  }

  function showAllResults() {
    if (!trimmed) return;
    // Everything the dropdown can find, not just services — /search shows
    // the same seven groups in full (SearchResults.jsx).
    navigate(`/search?q=${encodeURIComponent(trimmed)}`);
    setOpen(false);
    inputRef.current?.blur();
  }

  function handleKeyDown(e) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setHighlighted((i) => Math.min(i + 1, flat.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlighted((i) => Math.max(i - 1, -1));
    } else if (e.key === 'Home' && open && flat.length) {
      e.preventDefault();
      setHighlighted(0);
    } else if (e.key === 'End' && open && flat.length) {
      e.preventDefault();
      setHighlighted(flat.length - 1);
    } else if (e.key === 'Escape') {
      setOpen(false);
      inputRef.current?.blur();
    }
  }

  const isHero = variant === 'hero';

  // Cycle the example in the placeholder while the field is idle. Stops the
  // moment someone focuses or types, and never runs for reduced motion.
  useEffect(() => {
    if (isHero || placeholder || query || open) return undefined;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return undefined;
    const t = setInterval(() => setExampleIdx((i) => (i + 1) % EXAMPLES.length), EXAMPLE_MS);
    return () => clearInterval(t);
  }, [isHero, placeholder, query, open]);

  // ⌘K / Ctrl+K from anywhere, or "/" when you're not already typing, jumps
  // to the search field. Only the topbar instance listens, so the Home hero
  // (which renders a second SiteSearch) never fights it for the key.
  useEffect(() => {
    if (isHero) return undefined;
    const onKey = (e) => {
      const typing = e.target instanceof HTMLElement
        && (e.target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(e.target.tagName));
      const cmdK = (e.metaKey || e.ctrlKey) && !e.altKey && e.key.toLowerCase() === 'k';
      if (cmdK || (e.key === '/' && !typing && !e.metaKey && !e.ctrlKey && !e.altKey)) {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isHero]);

  // Keep the highlighted row in view while arrowing through a long list.
  useEffect(() => {
    if (highlighted < 0) return;
    document.getElementById(`${baseId}-opt-${highlighted}`)?.scrollIntoView({ block: 'nearest' });
  }, [highlighted, baseId]);

  const showDropdown = open && (flat.length > 0 || trimmed.length >= 2);
  const resolvedPlaceholder = placeholder || (isHero
    ? 'Search services, jobs, courses, people…'
    : `Try “${EXAMPLES[exampleIdx]}”`);
  const listId = `${baseId}-list`;

  // Combobox pattern (WAI-ARIA 1.2): focus never leaves the input; the
  // listbox below is driven by aria-activedescendant, so a screen reader
  // hears each row as the arrow keys move and knows a list is open at all.
  const comboProps = {
    name: 'query',
    ref: inputRef,
    type: 'text',
    role: 'combobox',
    'aria-expanded': showDropdown,
    'aria-controls': listId,
    'aria-autocomplete': 'list',
    'aria-activedescendant': showDropdown && highlighted >= 0 ? `${baseId}-opt-${highlighted}` : undefined,
    'aria-label': 'Search UniFreelancer',
    value: query,
    onChange: (e) => { setQuery(e.target.value); setOpen(true); },
    onFocus: () => setOpen(true),
    onBlur: () => setTimeout(() => setOpen(false), 150),
    onKeyDown: handleKeyDown,
    placeholder: resolvedPlaceholder,
    autoComplete: 'off',
  };

  // The hero variant (Home page) is a large pill on a dark background —
  // visually its own thing — but shares every bit of matching/keyboard/
  // dropdown logic above with the compact topbar variant. Only the input
  // chrome differs; the results panel below is identical either way.
  return (
    <form
      onSubmit={handleSubmit}
      role="search"
      className={`relative flex flex-1 ${isHero ? 'uf-hero-search max-w-[560px]' : 'uf-topbar-search max-w-[680px]'}`}
    >
      {isHero ? (
        <div className="flex w-full items-center overflow-hidden rounded-full border border-press-paper/20 bg-press-paper/95 shadow-[0_12px_40px_rgba(0,0,0,0.35)] focus-within:ring-2 focus-within:ring-press-paper focus-within:ring-offset-2 focus-within:ring-offset-press-ink">
          <input
            {...comboProps}
            className="min-w-0 flex-1 bg-transparent px-6 py-3.5 text-sm text-press-ink outline-none placeholder:text-press-muted phone:text-[16px]"
          />
          <button
            type="submit"
            aria-label="Search"
            className="m-1.5 flex items-center gap-2 rounded-full bg-press-ink px-5 py-2.5 text-sm font-semibold text-press-paper transition hover:bg-press-ink/90"
          >
            <PiMagnifyingGlass size={15} aria-hidden="true" />
            Search
          </button>
        </div>
      ) : (
        // Fiverr/Etsy pattern (option 1, chosen 2026-10-02): a white field with
        // a firm navy outline and its own button. Ctrl K and "/" still focus it
        // (the listener above); the shortcut just isn't drawn, since a key-hint
        // chip is a developer-tool convention that made this read as a template.
        <div className="flex h-11 w-full items-center gap-2 rounded-full bg-surface pl-[18px] pr-1 shadow-[inset_0_0_0_1.5px_#1A2230] transition-shadow duration-300 ease-spring focus-within:shadow-[inset_0_0_0_2px_#1A2230] motion-reduce:transition-none">
          <input
            {...comboProps}
            className="h-full min-w-0 flex-1 bg-transparent font-search text-[15px] phone:text-[16px] text-dark-primary outline-none selection:bg-press-brass-tint selection:text-press-ink placeholder:text-ink-soft focus-visible:shadow-none"
          />
          <button
            type="submit"
            aria-label="Search"
            className="grid h-9 w-9 flex-none place-items-center rounded-full bg-dark-primary text-white transition-[background-color,transform] duration-300 ease-spring hover:bg-dark-secondary active:scale-[0.96] motion-reduce:transition-none"
          >
            <PiMagnifyingGlassLight size={17} aria-hidden="true" />
          </button>
        </div>
      )}

      {/* Screen-reader result count, announced as it changes. */}
      <span className="sr-only" aria-live="polite">
        {showDropdown ? `${flat.length} result${flat.length === 1 ? '' : 's'}` : ''}
      </span>

      {showDropdown && (
        <div
          className={`absolute top-[calc(100%+8px)] z-[60] rounded-3xl bg-press-paper p-1.5 font-search shadow-[0_0_0_1px_rgba(18,22,28,0.06)] animate-scale-in origin-top motion-reduce:animate-none ${
            isHero ? 'inset-x-0' : 'right-0 w-[min(540px,calc(100vw-32px))]'
          }`}
        >
          <div className="overflow-hidden rounded-[18px] bg-press-card shadow-[0_1px_2px_rgba(18,22,28,0.05),0_24px_48px_-24px_rgba(18,22,28,0.35)]">
            <div id={listId} role="listbox" aria-label="Search results" className="max-h-[min(60vh,440px)] overflow-y-auto p-1.5">
              {flat.length === 0 ? (
                <p className="px-3 py-6 text-center text-sm text-press-muted">No matches for &ldquo;{trimmed}&rdquo;</p>
              ) : (
                groups.map((g) => (
                  <div key={g.key} role="group" aria-labelledby={`${baseId}-g-${g.key}`}>
                    <p id={`${baseId}-g-${g.key}`} className="px-2.5 pb-1 pt-2.5 text-xs font-bold text-press-muted">
                      {GROUP_LABEL[g.key]}
                    </p>
                    {g.items.map((item) => {
                      const idx = flat.indexOf(item);
                      const isHighlighted = idx === highlighted;
                      const Icon = GROUP_ICON[g.key];
                      return (
                        <div
                          key={item.id}
                          id={`${baseId}-opt-${idx}`}
                          role="option"
                          aria-selected={isHighlighted}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => goTo(item.path)}
                          onMouseMove={() => { if (!isHighlighted) setHighlighted(idx); }}
                          className={`flex cursor-pointer items-center gap-3 rounded-xl px-2.5 py-2 ${isHighlighted ? 'bg-press-paper-2' : ''}`}
                        >
                          {item.avatar ? (
                            <img loading="lazy" src={item.avatar} alt="" className="h-8 w-8 flex-none rounded-full object-cover" />
                          ) : (
                            <span className={`flex h-8 w-8 flex-none items-center justify-center rounded-[10px] text-press-muted ${isHighlighted ? 'bg-press-card' : 'bg-press-paper-2'}`}>
                              <Icon size={14} aria-hidden="true" />
                            </span>
                          )}
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-[15px] font-medium text-press-ink"><Highlight text={item.label} query={trimmed} /></span>
                            {item.sub && <span className="block truncate text-xs text-press-muted">{item.sub}</span>}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ))
              )}
            </div>
            {trimmed && (
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={showAllResults}
                className="flex w-full items-center justify-between gap-3 border-t border-press-border px-3.5 py-2.5 text-left text-[14px] text-press-muted transition-colors hover:bg-press-paper-2/60 hover:text-press-ink"
              >
                <span className="min-w-0 truncate">See all results for <span className="font-semibold text-press-ink">&ldquo;{trimmed}&rdquo;</span></span>
                <PiArrowRightLight size={15} aria-hidden="true" className="flex-none" />
              </button>
            )}
          </div>
        </div>
      )}
    </form>
  );
}

SiteSearch.propTypes = {
  user: PropTypes.object,
  variant: PropTypes.oneOf(['topbar', 'hero']),
  placeholder: PropTypes.string,
};

SiteSearch.defaultProps = {
  user: null,
  variant: 'topbar',
  placeholder: '',
};
